// Transaction Detail Sheet Component
import { useState, useMemo } from "react";
import { MessageSquare, Phone, Mail, FileText, AlertCircle, CheckCircle2, Clock, User, ChevronDown, Info, List, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";
import { useCompany } from "@/contexts/CompanyContext";
import { type Transaction } from "@/types/transaction";
import { TransactionTypeBadge } from "./transaction-type-badge";
import { TransactionStatusBadge } from "./transaction-status-badge";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { cn } from "@/lib/utils";
import PaymentLinkButton  from "./poc-payment-link-button";
import { useTransactionRecentActivities, type TransactionActivity } from "@/hooks/useTransactionRecentActivities";
import { useTransactionNotes } from "@/hooks/useTransactionNotes";
import { ViewTransactionDocumentButton } from "./view-document-button";
import { TransactionDocumentsTab } from "./transaction-documents-tab/transaction-documents-tab";

interface TransactionDetailSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly transaction: Transaction | null;
}

// Activity types for the transaction
type ActivityType = TransactionActivity['type'];

export function TransactionDetailSheet({
  open,
  onOpenChange,
  transaction,
}: TransactionDetailSheetProps) {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const [activeTab, setActiveTab] = useState('details');
  const [selectedActivityFilters, setSelectedActivityFilters] = useState<ActivityType[]>([
    'note',
    'communication',
    'collection',
    'promise',
    'dispute',
  ]);
  const [isBasicInfoOpen, setIsBasicInfoOpen] = useState(true);
  const [isDetailLinesOpen, setIsDetailLinesOpen] = useState(true);
  
  // Default line items columns configuration
  const defaultLineItemColumns: ColumnDefinition[] = useMemo(() => {
    const baseCols: ColumnDefinition[] = [
      { id: "itemCode", label: "Item Code", visible: true, order: 0 },
      { id: "description", label: "Description", visible: true, locked: true, order: 1 },
      { id: "project", label: "Project", visible: true, order: 2 },
      { id: "projectName", label: "Project Name", visible: false, order: 3 },
      { id: "costCentre", label: "Cost Centre", visible: true, order: 4 },
      { id: "costCentreName", label: "Cost Centre Name", visible: false, order: 5 },
      { id: "quantity", label: "Qty", visible: true, locked: true, order: 6 },
      { id: "unitPrice", label: "Unit Price", visible: true, locked: true, order: 7 },
      { id: "subtotal", label: "Net", visible: true, locked: true, order: 8 },
      { id: "taxAmount", label: "Tax", visible: true, order: 9 },
      { id: "total", label: "Gross", visible: true, locked: true, order: 10 },
    ];
    return baseCols;
  }, []);
  const [lineItemColumns, setLineItemColumns] = useState<ColumnDefinition[]>(defaultLineItemColumns);

  // Fetch activities from API (or mock in demo mode)
  const { data: recentActivities = [], isLoading: isRecentActivitiesLoading } = useTransactionRecentActivities(
    transaction?.id,
    transaction?.amount
  );

  // Fetch transaction notes
  const { data: transactionNotes = [], isLoading: isNotesLoading } = useTransactionNotes(transaction?.id);

  const isActivitiesLoading = isRecentActivitiesLoading || isNotesLoading;

  // Merge transaction notes into the activities list
  const activities: TransactionActivity[] = useMemo(() => {
    const noteActivities: TransactionActivity[] = transactionNotes.map((note) => ({
      id: `note-${note.id}`,
      type: 'note' as const,
      title: 'Note added',
      description: note.content,
      createdBy: note.createdBy.name,
      createdAt: new Date(note.createdAt),
    }));

    // Filter out 'note' type from recentActivities to avoid duplicates (API may also return note activities)
    const nonNoteActivities = recentActivities.filter((a) => a.type !== 'note');

    return [...noteActivities, ...nonNoteActivities];
  }, [transactionNotes, recentActivities]);

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return [...lineItemColumns]
      .sort((a, b) => a.order - b.order)
      .filter(col => col.visible);
  }, [lineItemColumns]);

  const handleApplyColumns = (columns: ColumnDefinition[]) => {
    setLineItemColumns(columns);
  };

  const handleResetColumns = () => {
    setLineItemColumns(defaultLineItemColumns);
  };

  // Early return if no transaction - must be after all hooks but before any transaction access
  if (!transaction) return null;

  const isForeignTransaction = transaction.currency !== selectedCompany?.currency;
  const hasLineItems = transaction.lineItems && transaction.lineItems.length > 0;

  // Filter activities based on selected filters
  const filteredActivities = selectedActivityFilters.length === 0
    ? activities
    : activities.filter((activity) => selectedActivityFilters.includes(activity.type));

  // Sort activities by date (most recent first)
  const sortedActivities = [...filteredActivities].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );

  // Toggle activity filter
  const toggleActivityFilter = (type: ActivityType) => {
    setSelectedActivityFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedActivityFilters(['note', 'communication', 'collection', 'promise', 'dispute']);
  };

  // Get activity icon
  const getActivityIcon = (type: ActivityType, metadata?: TransactionActivity['metadata']) => {
    switch (type) {
      case 'note':
        return <MessageSquare className="h-4 w-4" />;
      case 'communication':
        if (metadata?.method === 'phone') {
          return <Phone className="h-4 w-4" />;
        }
        return <Mail className="h-4 w-4" />;
      case 'collection':
        return <AlertCircle className="h-4 w-4" />;
      case 'promise':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'dispute':
        return <FileText className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  // Get activity color
  const getActivityColor = (type: ActivityType) => {
    switch (type) {
      case 'note':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
      case 'communication':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'collection':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      case 'promise':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'dispute':
        return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  // Get activity badge color
  const getActivityBadgeColor = (type: ActivityType) => {
    switch (type) {
      case 'note':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
      case 'communication':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'collection':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      case 'promise':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'dispute':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  // Get activity type label
  const getActivityTypeLabel = (type: ActivityType) => {
    switch (type) {
      case 'note':
        return 'Note';
      case 'communication':
        return 'Communication';
      case 'collection':
        return 'Collection';
      case 'promise':
        return 'Promise';
      case 'dispute':
        return 'Dispute';
      default:
        return type;
    }
  };

  // Count activities by type
  const activityCounts = {
    note: activities.filter((a) => a.type === 'note').length,
    communication: activities.filter((a) => a.type === 'communication').length,
    collection: activities.filter((a) => a.type === 'collection').length,
    promise: activities.filter((a) => a.type === 'promise').length,
    dispute: activities.filter((a) => a.type === 'dispute').length,
  };

  return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-5xl p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle>Transaction Details</SheetTitle>
            <SheetDescription>
              Complete information for {transaction.referenceNumber}
            </SheetDescription>
          </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 pt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="activity">
                Activity
                {sortedActivities.length > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                    {sortedActivities.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex-1 mt-0">
            <div className="h-[calc(100vh-240px)] overflow-y-auto">
                <div className="space-y-6 px-6 py-4">
                  {/* Basic Information - Collapsible */}
                  <Collapsible open={isBasicInfoOpen} onOpenChange={setIsBasicInfoOpen}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                          <Info className="h-4 w-4 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold">Basic Information</h3>
                      </div>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2 h-8">
                          <span className="text-xs font-medium">
                            {isBasicInfoOpen ? "Collapse" : "Expand"}
                          </span>
                          <ChevronDown className={cn(
                            "h-3.5 w-3.5 transition-transform duration-200",
                            isBasicInfoOpen && "rotate-180"
                          )} />
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="mt-4">
                      <div className="grid grid-cols-4 gap-x-6 gap-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Reference Number</p>
                        <p className="font-medium">{transaction.referenceNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Transaction ID</p>
                        <p className="font-medium">{transaction.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Transaction Date</p>
                        <p className="font-medium">{formatDateDDMMYYYY(transaction.transactionDate)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Customer Name</p>
                        <button
                          onClick={() => navigate(`/customers/${transaction.customerId}`, { state: { customer: { name: transaction.customerName, accountNumber: transaction.accountNumber } } })}
                          className="text-primary hover:underline font-medium transition-colors text-left"
                          title={`View details for ${transaction.customerName}`}
                        >
                          {transaction.customerName}
                        </button>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Transaction Type</p>
                        <div className="mt-1">
                          <TransactionTypeBadge type={transaction.transactionType} />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <div className="mt-1">
                          {transaction.paymentLinkStatus ? (
                            <Badge variant="outline" className="capitalize">
                              {transaction.paymentLinkStatus.replace(/_/g, " ")}
                            </Badge>
                          ) : (
                            <TransactionStatusBadge status={transaction.status} />
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Amount</p>
                        <p className="text-xl font-bold">
                          {formatCurrency(transaction.displayAmount ?? transaction.amount, transaction.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Currency</p>
                        <p className="font-medium">{transaction.currency}</p>
                      </div>
                      {transaction.dueDate && (
                        <div>
                          <p className="text-sm text-muted-foreground">Due Date</p>
                          <p className="font-medium">{formatDateDDMMYYYY(transaction.dueDate)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-muted-foreground">Created By</p>
                        <p className="font-medium">{transaction.createdBy}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateDDMMYYYY(transaction.createdAt)}
                        </p>
                      </div>
                      {transaction.modifiedBy && (
                        <div>
                          <p className="text-sm text-muted-foreground">Modified By</p>
                          <p className="font-medium">{transaction.modifiedBy}</p>
                          {transaction.modifiedAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateDDMMYYYY(transaction.modifiedAt)}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="col-span-4">
                        <p className="text-sm text-muted-foreground">Description</p>
                        <p className="font-medium">{transaction.description}</p>
                      </div>
                      {transaction.notes && (
                        <div className="col-span-4">
                          <p className="text-sm text-muted-foreground">Notes</p>
                          <p className="font-medium">{transaction.notes}</p>
                        </div>
                      )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Detail Lines - Collapsible */}
                  {hasLineItems && (
                    <>
                      <Separator />
                      <Collapsible open={isDetailLinesOpen} onOpenChange={setIsDetailLinesOpen}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                              <List className="h-4 w-4 text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold">Detail Lines</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <ColumnConfigDialog
                              columns={lineItemColumns}
                              onApply={handleApplyColumns}
                              onReset={handleResetColumns}
                            />
                            <CollapsibleTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-2 h-8">
                                <span className="text-xs font-medium">
                                  {isDetailLinesOpen ? "Collapse" : "Expand"}
                                </span>
                                <ChevronDown className={cn(
                                  "h-3.5 w-3.5 transition-transform duration-200",
                                  isDetailLinesOpen && "rotate-180"
                                )} />
                              </Button>
                            </CollapsibleTrigger>
                          </div>
                        </div>
                        <CollapsibleContent className="mt-4">
                        <div className="rounded-md border overflow-x-auto">
                          <Table className="min-w-max">
                            <TableHeader>
                              <TableRow>
                                {visibleColumns.map((column) => (
                                  <TableHead
                                    key={column.id}
                                    className={cn(
                                      column.id === 'quantity' || 
                                      column.id === 'unitPrice' || 
                                      column.id === 'subtotal' || 
                                      column.id === 'taxAmount' || 
                                      column.id === 'total'
                                        ? 'text-right'
                                        : ''
                                    )}
                                  >
                                    {column.label}
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {transaction.lineItems!.map((item) => (
                                <TableRow key={item.id}>
                                  {visibleColumns.map((column) => {
                                    const isRightAlign = 
                                      column.id === 'quantity' || 
                                      column.id === 'unitPrice' || 
                                      column.id === 'subtotal' || 
                                      column.id === 'taxAmount' || 
                                      column.id === 'total' ||
                                      column.id === 'currencyGross' ||
                                      column.id === 'currencyNet' ||
                                      column.id === 'currencyVAT';
                                    
                                    let cellContent: React.ReactNode = '-';
                                    
                                    switch (column.id) {
                                      case 'itemCode':
                                        cellContent = item.itemCode || '-';
                                        break;
                                      case 'description':
                                        cellContent = item.description;
                                        break;
                                      case 'project':
                                        cellContent = item.project || '-';
                                        break;
                                      case 'costCentre':
                                        cellContent = item.costCentre || '-';
                                        break;
                                      case 'projectName':
                                        cellContent = item.projectName || '-';
                                        break;
                                      case 'costCentreName':
                                        cellContent = item.costCentreName || '-';
                                        break;
                                      case 'quantity':
                                        cellContent = item.quantity;
                                        break;
                                      case 'unitPrice':
                                        cellContent = formatCurrency(item.unitPrice, transaction.currency);
                                        break;
                                      case 'subtotal':
                                        cellContent = formatCurrency(isForeignTransaction ? item.currencyNet ?? item.subtotal : item.subtotal, transaction.currency);
                                        break;
                                      case 'taxAmount': {
                                        const taxVal = isForeignTransaction ? item.currencyVAT ?? item.taxAmount : item.taxAmount;
                                        cellContent = taxVal != null ? formatCurrency(taxVal, transaction.currency) : '-';
                                        break;
                                      }
                                      case 'total':
                                        cellContent = formatCurrency(isForeignTransaction ? item.currencyGross ?? item.total : item.total, transaction.currency);
                                        break;
                                      case 'currencyGross':
                                        cellContent = item.currencyGross
                                          ? formatCurrency(item.currencyGross, transaction.currency)
                                          : '-';
                                        break;
                                      case 'currencyNet':
                                        cellContent = item.currencyNet
                                          ? formatCurrency(item.currencyNet, transaction.currency)
                                          : '-';
                                        break;
                                      case 'currencyVAT':
                                        cellContent = item.currencyVAT
                                          ? formatCurrency(item.currencyVAT, transaction.currency)
                                          : '-';
                                        break;
                                    }
                                    
                                    return (
                                      <TableCell
                                        key={column.id}
                                        className={cn(
                                          isRightAlign && 'text-right',
                                          column.id === 'total' && 'font-bold'
                                        )}
                                      >
                                        {cellContent}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                            <TableFooter>
                              <TableRow>
                                <TableCell
                                  colSpan={visibleColumns.length - 1}
                                  className="text-right font-semibold"
                                >
                                  Gross Total
                                </TableCell>
                                <TableCell className="text-right font-bold text-lg">
                                  {formatCurrency(
                                    transaction.lineItems!.reduce((sum, item) => sum + (isForeignTransaction ? (item.currencyGross ?? item.total) : item.total), 0),
                                    transaction.currency
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </>
                  )}
                </div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="flex-1 mt-0">
            <div className="flex flex-col h-[calc(100vh-240px)]">
              {/* Activity Filters */}
              <div className="px-6 py-4 border-b bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">Filter by Type</h3>
                  {selectedActivityFilters.length < 5 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-7 text-xs"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(['note', 'communication', 'collection', 'promise', 'dispute'] as ActivityType[]).map((type) => (
                    <Badge
                      key={type}
                      variant="outline"
                      className={cn(
                        'cursor-pointer transition-all hover:scale-105',
                        selectedActivityFilters.includes(type)
                          ? getActivityBadgeColor(type)
                          : 'bg-background text-muted-foreground border-muted'
                      )}
                      onClick={() => toggleActivityFilter(type)}
                    >
                      {getActivityTypeLabel(type)}
                      <span className="ml-1.5 text-xs opacity-70">
                        ({activityCounts[type]})
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Activity Timeline */}
              <ScrollArea className="flex-1">
                <ScrollAreaViewport className="h-full w-full">
                  <div className="px-6 py-4">
                    {isActivitiesLoading ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Loader2 className="h-8 w-8 text-muted-foreground/50 mb-4 animate-spin" />
                        <p className="text-sm text-muted-foreground">
                          Loading activities...
                        </p>
                      </div>
                    ) : sortedActivities.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                        <p className="text-sm text-muted-foreground">
                          No activities found matching your filters
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearAllFilters}
                          className="mt-4"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {sortedActivities.map((activity, index) => (
                          <div key={activity.id} className="relative">
                            {/* Timeline line */}
                            {index < sortedActivities.length - 1 && (
                              <div className="absolute left-5 top-12 bottom-0 w-px bg-border" />
                            )}

                            {/* Activity card */}
                            <div className="flex gap-4">
                              {/* Icon */}
                              <div
                                className={cn(
                                  'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                                  getActivityColor(activity.type)
                                )}
                              >
                                {getActivityIcon(activity.type, activity.metadata)}
                              </div>

                              {/* Content */}
                              <div className="flex-1 pb-4">
                                <div className="flex items-start justify-between gap-4 mb-1">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-sm">{activity.title}</h4>
                                      <Badge
                                        variant="outline"
                                        className={cn('text-xs', getActivityBadgeColor(activity.type))}
                                      >
                                        {getActivityTypeLabel(activity.type)}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {activity.description}
                                    </p>

                                    {/* Metadata */}
                                    {activity.metadata && (
                                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                                        {activity.metadata.method && (
                                          <span className="flex items-center gap-1">
                                            {activity.metadata.method === 'phone' ? (
                                              <Phone className="h-3 w-3" />
                                            ) : (
                                              <Mail className="h-3 w-3" />
                                            )}
                                            {activity.metadata.method}
                                          </span>
                                        )}
                                        {activity.metadata.status && (
                                          <span className="flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            {activity.metadata.status}
                                          </span>
                                        )}
                                        {activity.metadata.amount && (
                                          <span className="flex items-center gap-1 font-medium">
                                            {formatCurrency(activity.metadata.amount, transaction.currency)}
                                          </span>
                                        )}
                                        {activity.metadata.dueDate && (
                                          <span className="flex items-center gap-1">
                                            Due: {activity.metadata.dueDate}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                  <User className="h-3 w-3" />
                                  <span>{activity.createdBy}</span>
                                  <span>•</span>
                                  <span>{formatDateDDMMYYYY(activity.createdAt.toISOString())}</span>
                                  <span>•</span>
                                  <span>
                                    {activity.createdAt.toLocaleTimeString('en-GB', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>
          </TabsContent>

            <TabsContent value="documents" className="flex-1 mt-0">
              <div className="h-[calc(100vh-240px)] overflow-y-auto">
                <div className="px-6 pt-4">
                  <ViewTransactionDocumentButton
                    transactionId={transaction.id}
                    transactionType={transaction.transactionType}
                  />
                </div>
                <TransactionDocumentsTab
                  transactionReference={transaction.referenceNumber}
                  externalId={transaction.externalId}
                  accountCode={transaction.accountCode}
                  customerName={transaction.customerName}
                  headerReference={transaction.headerReference}
                  transactionDate={transaction.transactionDate}
                  description={transaction.description}
                  transactionType={transaction.transactionType}
                  customerId={transaction.customerId?.toString()}
                  accountNumber={transaction.accountNumber}
                />
              </div>
            </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        <SheetFooter className="px-6 py-4 bg-background border-t flex gap-2">
            <div className="flex gap-2">
              <ViewTransactionDocumentButton
                transactionId={transaction.id}
                transactionType={transaction.transactionType}
              />
              {/* Temporarily hidden - Send Payment Reminder button */}
              {/* <PaymentLinkButton
                customerName={transaction.customerName}
                customerEmail="victor.seceleanu@theaccessgroup.com"
                description={transaction.description}
                externalReference={transaction.referenceNumber}
                items={
                  transaction.lineItems && transaction.lineItems.length > 0
                    ? transaction.lineItems.map((item) => ({
                        description: item.description,
                        quantity: item.quantity,
                        unitPrice: Math.round((item.total / item.quantity) * 100),
                      }))
                    : [
                        {
                          description: transaction.description,
                          quantity: 1,
                          unitPrice: Math.round(transaction.amount * 100),
                        },
                      ]
                }
                metadata={{ invoiceId: transaction.referenceNumber, source: "portal" }}
              /> */}
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                Close
              </Button>
            </div>
          </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
